import { Component, signal, inject, OnInit } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { trigger, transition, style, animate, query } from '@angular/animations';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { SidebarComponent } from './sidebar.component';
import { TopbarComponent } from './topbar.component';
import { AuthService } from '../auth/auth.service';

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [RouterOutlet, CommonModule, FormsModule, SidebarComponent, TopbarComponent, DialogModule, ButtonModule, InputTextModule, ToastModule],
  providers: [MessageService],
  template: `
    <p-toast />
    <div class="admin-layout">
      <app-sidebar
        [collapsed]="sidebarCollapsed()"
        [mobileOpen]="mobileOpen()"
        (toggleCollapse)="toggleSidebar()"
        (closeMobile)="mobileOpen.set(false)"
      />

      <app-topbar (menuToggle)="toggleMobile()" />

      <main class="layout-content" [class.sidebar-collapsed]="sidebarCollapsed()">
        <div class="content-wrapper" @routeAnimation>
          <router-outlet />
        </div>
      </main>
    </div>

    <!-- Welcome modal: set display name -->
    <p-dialog
      [(visible)]="showDisplayNameDialog"
      [modal]="true"
      [closable]="false"
      [draggable]="false"
      [style]="{width: '460px'}"
      [showHeader]="false"
      styleClass="dn-blur-backdrop"
    >
      <div class="dn-dialog">
        <div class="dn-dialog__mascot-wrap">
          <img
            [src]="isFemale ? 'images/welcome-female.png' : 'images/welcome-male.png'"
            alt=""
            class="dn-dialog__mascot"
          />
        </div>
        <h2 class="dn-dialog__title">{{ isFemale ? 'Bienvenida' : 'Bienvenido' }}!</h2>
        <p class="dn-dialog__subtitle">Como te gustaria que te llamemos?</p>
        <div class="dn-dialog__field">
          <input
            pInputText
            [(ngModel)]="displayNameInput"
            placeholder="Ej: Carlos Gamarra"
            class="dn-dialog__input"
            [disabled]="savingDisplayName()"
            (keydown.enter)="confirmDisplayName()"
          />
        </div>
        <small class="dn-dialog__hint">Solo se muestra una vez. Puedes cambiarlo despues en tu perfil.</small>
        @if (displayNameError) {
          <small class="dn-dialog__error">{{ displayNameError }}</small>
        }
        <p-button
          label="Confirmar"
          icon="pi pi-check"
          styleClass="w-full dn-confirm-btn"
          [loading]="savingDisplayName()"
          [disabled]="!displayNameInput.trim()"
          (onClick)="confirmDisplayName()"
        />
      </div>
    </p-dialog>
  `,
  styles: [`
    .dn-dialog {
      text-align: center;
      padding: 0;
    }
    .dn-dialog__mascot-wrap {
      margin: 0 auto .25rem;
    }
    .dn-dialog__mascot {
      width: 140px;
      height: auto;
      display: block;
      margin: 0 auto;
    }
    .dn-dialog__title {
      margin: 0 0 .25rem;
      font-size: 1.5rem;
      font-weight: 800;
      color: #25d07b;
    }
    .dn-dialog__subtitle {
      margin: 0 0 1.25rem;
      font-size: 1rem;
      color: var(--text-color-secondary, #64748b);
    }
    .dn-dialog__field {
      margin-bottom: .625rem;
    }
    .dn-dialog__input {
      width: 100%;
      text-align: center;
      font-size: 1.0625rem;
      font-weight: 500;
      letter-spacing: .01em;
    }
    .dn-dialog__hint {
      display: block;
      font-size: .75rem;
      color: var(--text-color-secondary, #94a3b8);
      margin-bottom: 1rem;
    }
    .dn-dialog__error {
      display: block;
      font-size: .8rem;
      color: #ef4444;
      margin-bottom: .75rem;
    }
    :host ::ng-deep .dn-blur-backdrop + .p-dialog-mask,
    :host ::ng-deep .p-dialog-mask:has(.dn-blur-backdrop) {
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
      background: rgba(0, 0, 0, 0.3) !important;
    }
    :host ::ng-deep .dn-confirm-btn {
      border-radius: 10px !important;
    }
    :host ::ng-deep .dn-blur-backdrop {
      border-radius: 20px !important;
    }
    :host ::ng-deep .dn-blur-backdrop .p-dialog-content {
      border-radius: 20px !important;
      padding: 1.5rem 2rem 2rem !important;
    }
  `],
  animations: [
    trigger('routeAnimation', [
      transition(':enter', [
        query(':enter', [
          style({ opacity: 0, transform: 'translateY(12px)' }),
          animate('0.4s cubic-bezier(0.22, 1, 0.36, 1)', style({ opacity: 1, transform: 'translateY(0)' })),
        ], { optional: true }),
      ]),
    ]),
  ],
})
export class LayoutComponent implements OnInit {
  private auth = inject(AuthService);
  private messageService = inject(MessageService);

  sidebarCollapsed = signal(false);
  mobileOpen = signal(false);
  showDisplayNameDialog = false;
  displayNameInput = '';
  savingDisplayName = signal(false);
  displayNameError = '';
  isFemale = false;

  private static readonly FEMALE_NAMES = new Set([
    'maria','ana','rosa','carmen','julia','lucia','sofia','elena','laura','diana',
    'andrea','claudia','patricia','sandra','monica','gabriela','carolina','daniela',
    'valentina','fernanda','alejandra','natalia','camila','mariana','paola','silvia',
    'susana','teresa','victoria','veronica','yolanda','cecilia','norma','gladys',
    'elizabeth','milagros','katherine','jessica','jennifer','vanessa','karina',
    'marisol','luz','flor','rocio','pilar','martha','nancy','gloria','isabel',
    'betty','ruth','miriam','olga','irma','edith','sonia','dora','lilia','nelly',
    'hilda','ines','eva','sara','raquel','angela','blanca','elsa','lidia','esther',
    'doris','magaly','magali','jackeline','jacqueline','roxana','gisela','carla',
    'lorena','ivonne','yvonne','fiorella','miluska','medalit','yanina','yesenia',
    'lesly','leslie','wendy','karen','kelly','shirley','melisa','melissa','johana',
    'juana','josefa','guadalupe','esperanza','consuelo','aurora','bertha','delia',
  ]);

  ngOnInit(): void {
    if (this.auth.needsDisplayName()) {
      this.displayNameInput = this.auth.suggestDisplayName();
      this.isFemale = this.detectFemale();
      this.showDisplayNameDialog = true;
    }
  }

  private detectFemale(): boolean {
    const user = this.auth.user();
    if (!user?.user_firstname) return false;
    const firstName = user.user_firstname.trim().split(/\s+/)[0].toLowerCase();
    if (LayoutComponent.FEMALE_NAMES.has(firstName)) return true;
    // Heurística: nombres que terminan en "a" (excepto comunes masculinos)
    const maleEndingA = new Set(['joshua','borja','garcia','garcia','nikita']);
    if (firstName.endsWith('a') && !maleEndingA.has(firstName)) return true;
    return false;
  }

  confirmDisplayName(): void {
    const name = this.displayNameInput.trim();
    if (!name) return;
    this.displayNameError = '';
    this.savingDisplayName.set(true);
    this.auth.updateDisplayName(name).subscribe({
      next: () => {
        this.auth.saveDisplayNameLocally(name);
        this.savingDisplayName.set(false);
        this.showDisplayNameDialog = false;
        this.messageService.add({ severity: 'success', summary: 'Listo', detail: `Bienvenido, ${name}` });
      },
      error: (err) => {
        this.savingDisplayName.set(false);
        this.displayNameError = err?.error?.message || 'No se pudo guardar. Intenta de nuevo.';
      },
    });
  }

  toggleSidebar(): void {
    this.sidebarCollapsed.update(v => !v);
  }

  toggleMobile(): void {
    this.mobileOpen.update(v => !v);
  }
}
